import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import api from '../../services/api';
import CalendarView from './CalendarView';
import { useWorkspace } from '../../utils/WorkspaceContext';

/**
 * @typedef {object} AttendeeData
 * @property {number} id - The ID of the attendee.
 * @property {string} full_name - The full name of the attendee.
 * @property {string} email - The email of the attendee.
 */

/**
 * @typedef {object} FormDataState
 * @property {string} meeting_name - Name of the meeting.
 * @property {string} url - URL for the meeting.
 * @property {dayjs.Dayjs | null} start_date - Start date of the meeting.
 * @property {dayjs.Dayjs | null} end_date - End date of the meeting.
 * @property {dayjs.Dayjs | null} start_time - Start time of the meeting.
 * @property {dayjs.Dayjs | null} end_time - End time of the meeting.
 * @property {boolean} is_recurring - Whether the meeting is recurring.
 * @property {boolean} is_weekly_recurring - Whether the meeting is weekly recurring.
 * @property {number[]} recurring_days - Array of selected weekdays (0=Monday, 6=Sunday).
 * @property {number[]} attendees - Array of selected attendee IDs.
 */

/**
 * @typedef {object} ApiMeetingData
 * @property {number} [id] - ID of the meeting (present if editing).
 * @property {string} meeting_name - Name of the meeting.
 * @property {string} url - URL for the meeting.
 * @property {string} start_date - Start date of the meeting (YYYY-MM-DD).
 * @property {string} end_date - End date of the meeting (YYYY-MM-DD).
 * @property {string} start_time - Start time of the meeting (HH:mm:ss).
 * @property {string} end_time - End time of the meeting (HH:mm:ss).
 * @property {boolean} is_recurring - Whether the meeting is recurring.
 * @property {boolean} is_weekly_recurring - Whether the meeting is weekly recurring.
 * @property {number[]} recurring_days - Array of selected weekdays (0=Monday, 6=Sunday).
 * @property {number[]} attendees - Array of selected attendee IDs.
 * @property {number} [created_by] - User ID of the creator.
 */

const WEEKDAYS = [
  { label: 'Monday', value: 0 },
  { label: 'Tuesday', value: 1 },
  { label: 'Wednesday', value: 2 },
  { label: 'Thursday', value: 3 },
  { label: 'Friday', value: 4 },
  { label: 'Saturday', value: 5 },
  { label: 'Sunday', value: 6 },
];

/**
 * Meetings component for managing and displaying meetings.
 * It includes functionality to create, edit, and view meetings in a calendar.
 * @component
 * @returns {JSX.Element} The Meetings component.
 */
function Meetings() {
  /** @type {[ApiMeetingData[], React.Dispatch<React.SetStateAction<ApiMeetingData[]>>]} */
  const [meetings, setMeetings] = useState([]);
  /** @type {[boolean, React.Dispatch<React.SetStateAction<boolean>>]} */
  const [open, setOpen] = useState(false);
  /** @type {[AttendeeData[], React.Dispatch<React.SetStateAction<AttendeeData[]>>]} */
  const [attendeesList, setAttendeesList] = useState([]);
  /** @type {[FormDataState, React.Dispatch<React.SetStateAction<FormDataState>>]} */
  const [formData, setFormData] = useState({
    meeting_name: '',
    url: '',
    start_date: null,
    end_date: null,
    start_time: null,
    end_time: null,
    is_recurring: false,
    is_weekly_recurring: false,
    recurring_days: [],
    attendees: [],
  });

  /** @type {[boolean, React.Dispatch<React.SetStateAction<boolean>>]} */
  const [isEditMode, setIsEditMode] = useState(false);
  /** @type {[number | null, React.Dispatch<React.SetStateAction<number | null>>]} */
  const [editingMeetingId, setEditingMeetingId] = useState(null);
  const { currentWorkspace } = useWorkspace();

  useEffect(() => {
    fetchMeetings().then(data => {
      if (data) setMeetings(data);
    });
    fetchAttendees();
  }, []);

  // Automatically set end_date to start_date if the meeting is not recurring.
  useEffect(() => {
    if (!formData.is_recurring && !formData.is_weekly_recurring && formData.start_date) {
      setFormData(prev => ({
        ...prev,
        end_date: formData.start_date,
      }));
    }
  }, [formData.is_recurring, formData.is_weekly_recurring, formData.start_date]);

  // Reset opposing recurring flags when one is selected
  useEffect(() => {
    if (formData.is_recurring) {
      setFormData(prev => ({
        ...prev,
        is_weekly_recurring: false,
        recurring_days: [],
      }));
    }
  }, [formData.is_recurring]);

  useEffect(() => {
    if (formData.is_weekly_recurring) {
      setFormData(prev => ({
        ...prev,
        is_recurring: false,
      }));
    }
  }, [formData.is_weekly_recurring]);

  /**
   * Fetches the list of meetings from the API.
   * @async
   * @returns {Promise<ApiMeetingData[] | undefined>} A promise that resolves to an array of meetings or undefined on error.
   */
  const fetchMeetings = async () => {
    try {
      const response = await api.get('meetings/');
      return response.data.payload;
    } catch (error) {
      console.error('Error fetching meetings:', error);
      return undefined;
    }
  };

  /**
   * Fetches the list of users (potential attendees) from the API.
   * @async
   * @returns {Promise<void>}
   */
  const fetchAttendees = async () => {
    try {
      const response = await api.get('assignees/', {params: { workspace_id: currentWorkspace.id }});
      setAttendeesList(response.data);
    } catch (error) {
      console.error('Error fetching attendees:', error);
    }
  };

  /**
   * Opens the dialog for creating or editing a meeting.
   * @returns {void}
   */
  const handleClickOpen = () => {
    setOpen(true);
  };

  /**
   * Closes the meeting creation/edit dialog and resets the form state.
   * @returns {void}
   */
  const handleClose = () => {
    setOpen(false);
    setIsEditMode(false);
    setEditingMeetingId(null);
    setFormData({
      meeting_name: '',
      url: '',
      start_date: null,
      end_date: null,
      start_time: null,
      end_time: null,
      is_recurring: false,
      is_weekly_recurring: false,
      recurring_days: [],
      attendees: [],
    });
  };

  /**
   * Handles changes to form input fields (TextField, Switch).
   * @param {React.ChangeEvent<HTMLInputElement>} event - The input change event.
   * @returns {void}
   */
  const handleInputChange = (event) => {
    const { name, value, checked, type } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' || type === 'switch' ? checked : value,
    }));
  };

  /**
   * Handles changes from DatePicker and TimePicker components.
   * @param {keyof Pick<FormDataState, 'start_date' | 'end_date' | 'start_time' | 'end_time'>} name - The name of the form data field to update.
   * @param {dayjs.Dayjs | null} value - The new date/time value from the picker.
   * @returns {void}
   */
  const handleDateChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value ? dayjs(value) : null,
    }));
  };

  /**
   * Toggles the selection state of an attendee.
   * @param {number} attendeeId - The ID of the attendee to toggle.
   * @returns {void}
   */
  const handleAttendeeToggle = (attendeeId) => {
    setFormData(prev => {
      const { attendees: currentAttendees } = prev;
      const newAttendees = currentAttendees.includes(attendeeId)
        ? currentAttendees.filter(id => id !== attendeeId)
        : [...currentAttendees, attendeeId];
      
      return {
        ...prev,
        attendees: newAttendees,
      };
    });
  };

  /**
   * Toggles the selection state of a weekday for weekly recurring meetings.
   * @param {number} dayValue - The weekday value to toggle (0=Monday, 6=Sunday).
   * @returns {void}
   */
  const handleDayToggle = (dayValue) => {
    setFormData(prev => {
      const { recurring_days: currentDays } = prev;
      const newDays = currentDays.includes(dayValue)
        ? currentDays.filter(day => day !== dayValue)
        : [...currentDays, dayValue].sort();
      
      return {
        ...prev,
        recurring_days: newDays,
      };
    });
  };

  /**
   * Prepares the form for editing an existing meeting.
   * Populates the form with the meeting's data and opens the dialog in edit mode.
   * @param {ApiMeetingData} meetingData - The data of the meeting to be edited.
   *                                    `start_time` and `end_time` are expected as 'HH:mm:ss' strings.
   * @returns {void}
   */
  const onEditMeeting = (meetingData) => {
    const today = dayjs().format('YYYY-MM-DD');
    setFormData({
      ...meetingData,
      start_date: dayjs(meetingData.start_date),
      end_date: dayjs(meetingData.end_date),
      start_time: meetingData.start_time
        ? dayjs(`${today}T${meetingData.start_time}`)
        : null,
      end_time: meetingData.end_time
        ? dayjs(`${today}T${meetingData.end_time}`)
        : null,
      recurring_days: meetingData.recurring_days || [],
    });
    setEditingMeetingId(meetingData.id ?? null);
    setIsEditMode(true);
    setOpen(true);
  };

  /**
   * Callback function triggered after a meeting is deleted.
   * Refreshes the list of meetings.
   * @async
   * @returns {Promise<void>}
   */
  const onMeetingDeleted = async () => {
    const updatedMeetings = await fetchMeetings();
    if (updatedMeetings) {
      setMeetings(updatedMeetings);
    }
  };

  /**
   * Handles the submission of the meeting form, either creating a new meeting or updating an existing one.
   * @async
   * @returns {Promise<void>}
   */
  const handleSubmit = async () => {
    try {
      const meetingPayload = {
        ...formData,
        start_date: formData.start_date ? formData.start_date.format('YYYY-MM-DD') : '',
        end_date: formData.end_date ? formData.end_date.format('YYYY-MM-DD') : (formData.start_date ? formData.start_date.format('YYYY-MM-DD') : ''),
        start_time: formData.start_time ? formData.start_time.format('HH:mm:ss') : '',
        end_time: formData.end_time ? formData.end_time.format('HH:mm:ss') : '',
      };

      let response;
      if (isEditMode && editingMeetingId) {
        response = await api.patch(`meetings/${editingMeetingId}/`, meetingPayload);
      } else {
        response = await api.post('meetings/', meetingPayload);
      }

      if (response.status === 200 || response.status === 201) {
        const updatedMeetings = await fetchMeetings();
        if (updatedMeetings) {
          setMeetings(updatedMeetings);
        }
        handleClose();
      }
    } catch (error) {
      console.error('Error submitting meeting:', error);
    }
  };

  const isRecurringMeeting = formData.is_recurring || formData.is_weekly_recurring;

  return (
    <Box sx={{ padding: 1 }}>
      <Button variant="contained" color="primary" onClick={handleClickOpen} sx={{ mb: 2 }}>
        Create Meeting
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditMode ? 'Update': 'Create New'} Meeting</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              name="meeting_name"
              label="Meeting Name"
              value={formData.meeting_name}
              onChange={handleInputChange}
              fullWidth
              required
            />
            
            <TextField
              name="url"
              label="Meeting URL"
              value={formData.url}
              onChange={handleInputChange}
              fullWidth
              required
            />

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Start Date"
                value={formData.start_date}
                onChange={(newValue) => handleDateChange('start_date', newValue)}
              />

              {isRecurringMeeting && (
                <DatePicker
                  label="End Date"
                  value={formData.end_date}
                  onChange={(newValue) => handleDateChange('end_date', newValue)}
                  minDate={formData.start_date || undefined}
                />
              )}

              <TimePicker
                label="Start Time"
                value={formData.start_time}
                onChange={(newValue) => handleDateChange('start_time', newValue)}
              />

              <TimePicker
                label="End Time"
                value={formData.end_time}
                onChange={(newValue) => handleDateChange('end_time', newValue)}
              />
            </LocalizationProvider>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_recurring}
                  onChange={handleInputChange}
                  name="is_recurring"
                />
              }
              label="Daily Recurring"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_weekly_recurring}
                  onChange={handleInputChange}
                  name="is_weekly_recurring"
                />
              }
              label="Weekly Recurring"
            />

            {formData.is_weekly_recurring && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Select Days of the Week
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {WEEKDAYS.map((day) => (
                    <Chip
                      key={day.value}
                      label={day.label}
                      clickable
                      color={formData.recurring_days.includes(day.value) ? 'primary' : 'default'}
                      onClick={() => handleDayToggle(day.value)}
                      variant={formData.recurring_days.includes(day.value) ? 'filled' : 'outlined'}
                    />
                  ))}
                </Box>
              </Box>
            )}

            <Box sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #ccc', borderRadius: 1 }}>
              <List dense>
                {attendeesList?.map((attendee) => (
                  <ListItem key={attendee.id} button onClick={() => handleAttendeeToggle(attendee.id)}>
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={formData.attendees.includes(attendee.id)}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText 
                      primary={`${attendee.full_name}`} 
                      secondary={attendee.email}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {isEditMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <CalendarView meetings={meetings} onEditMeeting={onEditMeeting} onMeetingDeleted={onMeetingDeleted} />
    </Box>
  );
}

export default Meetings;